// core
import { Navigate, useNavigate } from 'react-router-dom';
import { ReactNode, useEffect } from 'react';
import type { JSX } from 'react';
import { Spin } from 'antd';

import { CommonRouteObject } from './routerGenerator';
import Login from '@/pages/Login';
import StaticGEOReport from '@/pages/GEOReport/Static';
import Company from '@/pages/Company';
import Layout from '@/components/Layout';
import TaskList from '@/pages/TaskList';
import ArticleListPage from '@/pages/ArticleList';
import AnalysisListPage from '@/pages/AnalysisList';
import EnterpriseMonitorPage from '@/pages/EnterpriseMonitor';
import { useStore } from '@/stores';
import { useSetState } from 'ahooks';
import { USER_TOKEN } from '@/stores/user';
import UserListPage from '@/pages/UserList';
import GEOReportV2 from '@/pages/GEOReportV2';
import ArticleTemplateList from '@/pages/ArticleTemplateList';
import ArticleTypeList from '@/pages/ArticleTypeList';
import Customers from '@/pages/Customers';

// 注意左侧菜单包含的路由最好不要懒加载，不然会出现加载白屏
export function WithRouteHook(props: { children: ReactNode }): JSX.Element {
  const [state, setState] = useSetState({
    init: false,
  });
  const navigate = useNavigate();
  const userStore = useStore('user');

  const loginFn = async () => {
    // 首次访问的用户从来没有登录过，不应该弹出登录失效的消息
    if (!localStorage.getItem(USER_TOKEN)) {
      navigate('/login');
      return;
    }
    userStore
      .init()
      .then(() => {
        setState({
          init: true,
        });
      })
      .catch(() => {
        setState({
          init: true,
        });
        userStore.setUserInfo(null);
        navigate('/login');
      });
  };
  useEffect(() => {
    loginFn();
  }, []);

  if (!state.init) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return <>{props.children}</>;
}

function HomeRedirect() {
  const userStore = useStore('user');
  const to = userStore.info?.roleKey === 'CUSTOMER' ? '/customers' : '/company';
  return <Navigate to={to} replace />;
}
/** 公共路由(登录或者未登录都有的) */
const commonRoutes: CommonRouteObject[] = [
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/geoReport/:taskId',
    element: <GEOReportV2 />,
  },
  {
    path: '/geoReportOem/',
    element: <StaticGEOReport />,
  },
];
export const routes: CommonRouteObject[] = [
  {
    path: '/',
    element: (
      <WithRouteHook>
        <Layout />
      </WithRouteHook>
    ),
    children: [
      {
        index: true,
        element: <HomeRedirect />,
      },
      {
        path: 'company',
        handle: { title: '公司管理', name: '企业管理', isMenu: true, permission: ['company:view'] },
        children: [{ index: true, element: <Company /> }],
      },
      {
        path: 'tasks',
        handle: { title: '任务列表', name: '任务列表', isMenu: true, permission: ['task:view'] },
        element: <TaskList />,
      },
      {
        path: 'articles',
        handle: { title: '文章管理', name: '文章列表', isMenu: true, permission: ['article:view'] },
        element: <ArticleListPage />,
      },
      {
        path: 'analysis',
        handle: { title: '分析列表', name: '分析列表', isMenu: true, permission: ['analysis:view'] },
        element: <AnalysisListPage />,
      },
      {
        path: 'enterprise-monitor',
        handle: { title: '企业数据监控', name: '企业数据监控', isMenu: true, permission: ['company:dashboard:page'] },
        element: <EnterpriseMonitorPage />,
      },
      {
        path: 'article-templates',
        handle: { title: '发文模板', name: '发文模板', isMenu: true, permission: ['article_template:view'] },
        element: <ArticleTemplateList />,
      },
      {
        path: 'article-types',
        handle: { title: '文章类型', name: '文章类型', isMenu: true, permission: ['article_type:view'] },
        element: <ArticleTypeList />,
      },
      {
        path: 'users',
        handle: { title: '用户列表', name: '用户列表', isMenu: true, permission: ['sys_user:view'] },
        element: <UserListPage />,
      },
    ],
  },
  ...commonRoutes,
  {
    path: '*',
    element: <>404</>,
  },
  //
  // {
  //   path: 'customers/:companyId?/:section?',
  //   element: (
  //     <WithRouteHook>
  //       <Customers />
  //     </WithRouteHook>
  //   ),
  // },
];

export const unAuthRoutes: CommonRouteObject[] = [
  ...commonRoutes,
  // 兜底页默认为 /
  {
    path: '*',
    element: <Navigate to="/login" />,
  },
];
